import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEye,
  FaSearch,
  FaTh,
  FaBars,
  FaTimes,
  FaUser,
  FaDownload,FaPlus
} from "react-icons/fa";
import SummaryApi from "../common/index";
import { LiaUniversitySolid } from "react-icons/lia";
import { CiUser } from "react-icons/ci";
const LoaderSVG = () => (
  <svg
    className="animate-spin h-10 w-10 text-blue-600 dark:text-blue-400 mx-auto"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    course: "",
    email: "",
    mobile: "",
    enrollment: "",
     student_code: "",
  });
  const [showFilters, setShowFilters] = useState({
    name: false,
    course: false,
    email: false,
    mobile: false,
    enrollment: false,
         student_code: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const itemsPerPage = 12; // Updated to match CourseList

  // Theme handling
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem("admin-theme");
      const shouldBeDark = adminTheme === "dark";
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle("dark", shouldBeDark);
    };
    updateTheme();

    const handleThemeChange = () => updateTheme();
    const handleStorageChange = (e) => e.key === "admin-theme" && updateTheme();
    const handleAdminThemeToggled = () => updateTheme();

    window.addEventListener("themeChanged", handleThemeChange);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("adminThemeToggled", handleAdminThemeToggled);

    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adminThemeToggled", handleAdminThemeToggled);
    };
  }, []);

  const formatImageUrl = (imagePath) => {
    if (!imagePath || imagePath === "N/A") return "";
    if (imagePath.startsWith("http")) return imagePath;
    const baseUrl = SummaryApi.Images.url.replace("/api", "");
    if (imagePath.includes(baseUrl)) return imagePath;
    if (imagePath.startsWith("uploads/")) return `${baseUrl}/${imagePath}`;
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    fetchStudents();
    const handleFocus = () => fetchStudents();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  useEffect(() => {
    if (students.length > 0) {
      let filtered = [...students];

      if (searchTerm) {
        filtered = filtered.filter(
          (s) =>
            `${s.first_name} ${s.last_name}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            s.academic_course?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.mobile_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.stu_clg_enrollment_number
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );
      }

      filtered = filtered.filter(
        (s) =>
          (`${s.first_name} ${s.last_name}`
            .toLowerCase()
            .includes(columnFilters.name.toLowerCase()) || !columnFilters.name) &&
          (s.academic_course?.toLowerCase().includes(columnFilters.course.toLowerCase()) ||
            !columnFilters.course) &&
          (s.email?.toLowerCase().includes(columnFilters.email.toLowerCase()) ||
            !columnFilters.email) &&
          (s.mobile_number?.toLowerCase().includes(columnFilters.mobile.toLowerCase()) ||
            !columnFilters.mobile) &&
          (s.stu_clg_enrollment_number
            ?.toLowerCase()
            .includes(columnFilters.enrollment.toLowerCase()) || !columnFilters.enrollment)
      );

      setFilteredStudents(filtered);
      setCurrentPage(1);
    }
  }, [students, searchTerm, columnFilters]);

  const fetchStudents = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(SummaryApi.fetchstudentnames.url, {
        method: SummaryApi.fetchstudentnames.method,
      });
      const data = await response.json();
      if (data.status) setStudents(data.data || []);
      else toast.error(data.message || "Failed to fetch students");
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Error fetching students");
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivation = (student) => {
    setSelectedStudent(student);
    setShowActivationModal(true);
  };

  const confirmActivation = async () => {
    if (!selectedStudent) return;
    try {
      const response = await fetch(SummaryApi.StudentActivation.url, {
        method: SummaryApi.StudentActivation.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: selectedStudent.student_id,
          is_active: 1,
        }),
      });
      const data = await response.json();
      if (data.status) {
        toast.success("Student activated successfully!");
        fetchStudents();
      } else toast.error(data.message || "Failed to activate student");
    } catch (error) {
      toast.error("Error activating student");
    } finally {
      setShowActivationModal(false);
      setSelectedStudent(null);
    }
  };

  const handleView = (student) => {
    if (!student?.student_id) return;
    const encodedId = btoa(student.student_id.toString());
    window.open(`/admin-panel/student-dashboard?id=${encodedId}`, "_blank");
  };

  const downloadCSV = () => {
    const headers = [
      "Name",
      "Course",
      "Email",
      "Mobile",
      "Enrollment Number",
      "UG/PG",
      "Academic Year",
    ];
    const rows = filteredStudents.map((s) => [
      `${s.first_name} ${s.last_name}`,
      s.academic_course || "N/A",
      s.email || "N/A",
      s.mobile_number || "N/A",
      s.stu_clg_enrollment_number || "N/A",
      s.ug_pg || "N/A",
      s.academic_year || "N/A",
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "students.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFilter = (key) => {
    setShowFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setColumnFilters({
      name: "",
      course: "",
      email: "",
      mobile: "",
      enrollment: "",
           student_code: "",
    });
    setShowFilters({
      name: false,
      course: false,
      email: false,
      mobile: false,
      enrollment: false,
           student_code: "",
    });
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

  // Grid Item Renderer
  const renderGridItem = (student) => (
<div
  className={`p-3 flex flex-col h-full rounded-lg shadow-sm transition-all duration-300 border-2 border-solid ${
    isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-white"
  }`}
>
      <div className="flex justify-between items-start mb-3">
        <h3
          className={`text-sm font-semibold line-clamp-2 transition-colors ${
            isDarkMode ? "text-white" : "text-gray-800"
          }`}
        >
          {student.first_name} {student.last_name}
        </h3>
  <div className="flex gap-1">
          {student.is_self_registered === 1 && (
            <span className="px-2 py-0.5 text-[12px] rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
           <CiUser />
            </span>
          )}
          {student.is_self_registered === 0 && student.college_id === 0 && (
            <span className="px-2 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
              Admin
            </span>
          )}
          {student.is_self_registered === 0 && student.college_id > 0 && (
            <span className="px-2 py-0.5 text-[12px] rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
             <LiaUniversitySolid />
            </span>
          )}
          <span
            className={`px-2 py-0.5 text-xs rounded-full ${
              student.is_active
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
            }`}
          >
            {student.is_active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="mb-4">
        {student.student_image && student.student_image !== "N/A" ? (
          <img
            src={formatImageUrl(student.student_image)}
            alt={`${student.first_name} ${student.last_name}`}
            className="h-40 w-full object-cover rounded-md"
          />
        ) : (
          <div className="h-40 w-full rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              No Image
            </span>
          </div>
        )}
      </div>

      <div className="space-y-2 text-sm">
        <p className="text-gray-600 dark:text-gray-400">
          <strong>Academic Course:</strong> {student.academic_course || "N/A"}
        </p>
        <p className="text-gray-600 dark:text-gray-400 truncate">
          <strong>Email:</strong> {student.email || "N/A"}
        </p>
        <p className="text-gray-600 dark:text-gray-400">
          <strong>Mobile:</strong> {student.mobile_number || "N/A"}
        </p>
        <p className="text-gray-600 dark:text-gray-400">
          <strong>Enrollment Number:</strong> {student.student_code || "N/A"}
        </p>
      </div>

      <div className="mt-auto pt-4 flex justify-end">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleView(student);
          }}
          className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 text-sm"
        >
          <FaEye /> View Details
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen p-0 flex flex-col transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto w-full flex flex-col flex-grow">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1
            className={`text-2xl sm:text-3xl font-bold transition-colors ${
              isDarkMode ? "text-white" : "text-gray-800"
            }`}
          >
            Student Management
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md border transition-all ${
                  viewMode === "grid"
                    ? "bg-blue-900 text-white border-blue-600"
                    : isDarkMode
                    ? "text-gray-300 border-gray-600 hover:bg-blue-600 hover:text-white"
                    : "text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
                }`}
              >
                <FaTh />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md border transition-all ${
                  viewMode === "list"
                    ? "bg-blue-900 text-white border-blue-600"
                    : isDarkMode
                    ? "text-gray-300 border-gray-600 hover:bg-blue-600 hover:text-white"
                    : "text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
                }`}
              >
                <FaBars />
              </button>
            </div>
            <button
              onClick={downloadCSV}
              disabled={filteredStudents.length === 0}
              className={`px-4 py-1 rounded-md text-white font-medium transition-all shadow-lg ${
                filteredStudents.length === 0
                  ? "bg-gray-500 cursor-not-allowed"
                  : isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 shadow-blue-600/30"
                  : "bg-blue-900 hover:bg-blue-800 shadow-blue-700/30"
              }`}
            >
              <FaDownload className="inline mr-2" />
              Download Excel
            </button>
          </div>
        </div>

        {/* Main Card */}
        <div
          className={`rounded-xl shadow-lg flex flex-col flex-grow transition-all ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Search & Filters Bar */}
          <div
            className={`px-4 py-5 border-b flex flex-col sm:flex-row  items-start sm:items-center gap-4 transition-colors ${
              isDarkMode ? "bg-gray-800 border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-1 rounded-md border focus:outline-none focus:ring-2 transition-all ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white focus:ring-blue-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
              />
              <FaSearch
                className={`absolute left-3 top-2/4 -translate-y-1/2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
            </div>
            {(searchTerm || Object.values(columnFilters).some(Boolean)) && (
              <button
                onClick={clearFilters}
                className={`px-4 py-1 rounded-md text-white font-medium transition-all shadow-lg ${
                  isDarkMode
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-500 hover:bg-red-600"
                }`}
              >
                <FaTimes className="inline mr-2" />
                Clear Filters
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-4 flex-grow">
            {isLoading ? (
              <div className="grid grid-cols-1  sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-gray-200 dark:bg-gray-700 h-64 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            ) : filteredStudents.length === 0 ? (
         <div className="text-center py-16">
    <FaUser className="mx-auto text-6xl text-gray-400 mb-4" />
    <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
      No students found
    </p>
    <button
      onClick={() => window.location.href = `${window.location.origin}/admin-panel/Admin-student-create`}
      className={`px-6 py-3 rounded-md font-medium transition-all shadow-lg flex items-center gap-2 mx-auto ${
        isDarkMode
          ? "bg-blue-600 hover:bg-blue-700 text-white"
          : "bg-blue-900 hover:bg-blue-800 text-white"
      }`}
    >
      <FaPlus />
      Add Student
    </button>
  </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {currentStudents.map(renderGridItem)}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead
                    className={`sticky top-0 ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-50"
                    }`}
                  >
                    <tr>
                      <th className="px-4 py-3 text-left">Image</th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-2">
                          Name
                          <button onClick={() => toggleFilter("name")}>
                            <FaSearch className="text-blue-600" />
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left">Course</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Mobile</th>
                      <th className="px-4 py-3 text-left">Enrollment</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStudents.map((s) => (
                      <tr
                        key={s.student_id}
                        className={`border-t ${
                          isDarkMode
                            ? "border-gray-700 hover:bg-gray-700"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3">
                          {s.student_image && s.student_image !== "N/A" ? (
                            <img
                              src={formatImageUrl(s.student_image)}
                              alt=""
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-12 w-12 bg-gray-300 dark:bg-gray-600 rounded-full" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {s.first_name} {s.last_name}
                        </td>
                        <td className="px-4 py-3">{s.academic_course || "N/A"}</td>
                        <td className="px-4 py-3">{s.email || "N/A"}</td>
                        <td className="px-4 py-3">{s.mobile_number || "N/A"}</td>
                        <td className="px-4 py-3">{s.student_code || "N/A"}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              s.is_active
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            }`}
                          >
                            {s.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleView(s)}
                            className="text-blue-600 hover:underline mr-3"
                          >
                            <FaEye />
                          </button>
                          {!s.is_active && (
                            <button
                              onClick={() => handleActivation(s)}
                              className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                            >
                              Activate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <span
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredStudents.length)} of{" "}
                {filteredStudents.length} students
              </span>
              <div className="flex items-center gap-2">
                {["First", "Prev", "Next", "Last"].map((label) => {
                  const isPrev = label === "First" || label === "Prev";
                  const isNext = label === "Next" || label === "Last";
                  const disabled =
                    (isPrev && currentPage === 1) ||
                    (isNext && currentPage === totalPages);
                  return (
                    <button
                      key={label}
                      onClick={
                        label === "First"
                          ? handleFirstPage
                          : label === "Prev"
                          ? handlePrevPage
                          : label === "Next"
                          ? handleNextPage
                          : handleLastPage
                      }
                      disabled={disabled}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                        disabled
                          ? "bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-blue-900 text-white hover:bg-blue-800 shadow-lg"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
                <span className="px-3 py-1 text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activation Modal */}
      {showActivationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`rounded-lg p-6 w-full max-w-md ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDarkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Confirm Activation
            </h3>
            <p className={`mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              Activate {selectedStudent?.first_name} {selectedStudent?.last_name}?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowActivationModal(false)}
                className={`px-4 py-2 rounded-md border ${
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmActivation}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;